/**
 * #427 W2 — /my-schedule: a staffer's own week (closes #400).
 *
 * Dezirea (kennel tech), Jorge (groomer) and the walkers hold
 * `staff-schedule:read:all` after #665 D1 (they see the whole published
 * schedule on /schedule now), and the accountant holds
 * `staff-schedule:read:self` (#665 — this page closes its time-off dead
 * end). The own-week contract here is SERVER-side either way: the week
 * fetch sends `?self=1`, which narrows ANY caller to the verified JWT
 * subject's rows — this page does NO client-side filtering, it renders
 * exactly what comes back. Read-only by construction: there is no shift
 * write path here.
 *
 * The whole point of the screen is that a person can PLAN THEIR LIFE, so
 * future weeks are navigable without limit (the prev/next control walks
 * ±7 days from any week, so week+4 and beyond just work), and the
 * request-time-off form lives right underneath the week they're looking at.
 *
 * Design Philosophy principles served:
 *   P1 (one glance) — "you work Tue and Thu" is readable in one screen,
 *     no horizontal scroll, no facility-wide noise.
 *   P2 (next action obvious) — an empty week TEACHES ("You're not
 *     scheduled this week") instead of looking broken, and the page's one
 *     filled-primary is "Send request" on the time-off form.
 *   P3 (interface recedes) — the staffer's own hours are the biggest thing
 *     on the page; our chrome is small.
 *   P4 (feeling fast) — skeleton day cards while loading, never a blank
 *     screen or a spinner wall.
 *   P5 (never lie) — a pending request renders as PENDING, visually distinct
 *     from an approved one; a failed load is an error with a retry, never a
 *     silently empty week.
 *   P7 (built for hands) — this is read standing up on a phone: single
 *     column, ≥44px week controls, no hover-only affordances.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ClientConfig } from '@ferro/config';
import { PageHeader, Skeleton } from '@ferro/ui';
import {
  addIsoDays,
  fetchStaffWeek,
  fetchTimeOff,
  mondayIsoDate,
  readSession,
  TIME_OFF_TYPE_LABELS,
  todayIsoDate,
  cancelTimeOff,
  humanizeApiError,
  type StaffShift,
  type TimeOffRequest,
  type TimeOffType,
} from '../lib/api.js';
import { JobFunctionChip } from '../components/schedule/JobFunctionChip.js';
import { TimeOffRequestForm } from '../components/timeoff/TimeOffRequestForm.js';
import {
  formatDayRange,
  formatDayShort,
  formatTimeRange,
  isDayWithin,
} from '../lib/schedule-dates.js';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

type WeekState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; shifts: StaffShift[]; timeOff: TimeOffRequest[] };

function typeLabel(type: string): string {
  return TIME_OFF_TYPE_LABELS[type as TimeOffType] ?? 'Time off';
}

export function MySchedule({ config }: { config: ClientConfig }): JSX.Element {
  const apiUrl = config.deployment.apiUrl;
  const [weekStart, setWeekStart] = useState<string>(() => mondayIsoDate());
  const [state, setState] = useState<WeekState>({ kind: 'loading' });
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const today = todayIsoDate();

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    const { token } = readSession();
    if (!token) {
      setState({ kind: 'error', message: 'Your session expired. Sign in again.' });
      return;
    }
    try {
      // Both reads are server-narrowed to this staffer. The week read sends
      // `?self=1` (#665 PR-C): the floor roles hold staff-schedule:read:all
      // after D1, so WITHOUT the explicit self flag this page would render
      // the whole roster's week. The narrowing is JWT-pinned server-side —
      // this page still does NO client filtering. Time off has stayed
      // `:self`-narrowed by role throughout.
      const [week, timeOff] = await Promise.all([
        fetchStaffWeek(apiUrl, token, weekStart, undefined, true),
        fetchTimeOff(apiUrl, token),
      ]);
      setState({
        kind: 'ready',
        shifts: week.shifts ?? [],
        timeOff: timeOff.requests ?? [],
      });
    } catch (err) {
      setState({
        kind: 'error',
        message: humanizeApiError(err instanceof Error ? err.message : null),
      });
    }
  }, [apiUrl, weekStart]);

  useEffect(() => {
    void load();
  }, [load]);

  const weekDays = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => ({
        iso: addIsoDays(weekStart, i),
        label: DAY_LABELS[i]!,
      })),
    [weekStart],
  );

  const shiftsByDate = useMemo(() => {
    const map = new Map<string, StaffShift[]>();
    if (state.kind !== 'ready') return map;
    // Split shifts are REAL: one person can hold several shifts on one day
    // (open 06:00–10:00, close 16:00–20:00). Never collapse by staffer —
    // key by date and render every row.
    for (const s of state.shifts) {
      const arr = map.get(s.date) ?? [];
      arr.push(s);
      map.set(s.date, arr);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => a.start_time.localeCompare(b.start_time));
    }
    return map;
  }, [state]);

  const myTimeOff = state.kind === 'ready' ? state.timeOff : [];
  const activeTimeOff = myTimeOff.filter(
    (r) => r.status === 'approved' || r.status === 'pending',
  );
  const shiftCount = state.kind === 'ready' ? state.shifts.length : 0;

  const timeOffFor = (day: string): TimeOffRequest[] =>
    activeTimeOff.filter((r) => isDayWithin(day, r.start_date, r.end_date));

  const handleCancel = async (request: TimeOffRequest): Promise<void> => {
    const { token } = readSession();
    if (!token) return;
    setCancellingId(request.id);
    setCancelError(null);
    try {
      const { request: updated } = await cancelTimeOff(apiUrl, token, request.id);
      setState((curr) =>
        curr.kind === 'ready'
          ? {
              ...curr,
              timeOff: curr.timeOff.map((r) =>
                r.id === updated.id ? updated : r,
              ),
            }
          : curr,
      );
    } catch (err) {
      setCancelError(
        err instanceof Error
          ? err.message
          : 'We couldn’t withdraw that request. Try again.',
      );
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <section className="ferro-admin-page ferro-myschedule" aria-busy={state.kind === 'loading'}>
      <PageHeader
        title="My Schedule"
        subtitle="Your shifts and your time off — plan around them."
      />

      <div className="ferro-toolbar ferro-myschedule__weeknav" role="group" aria-label="Week">
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost"
          onClick={() => setWeekStart(addIsoDays(weekStart, -7))}
          aria-label="Previous week"
        >
          ← Prev
        </button>
        <span className="ferro-myschedule__weeklabel" data-testid="my-schedule-week-label">
          {formatDayRange(weekStart, addIsoDays(weekStart, 6))}
        </span>
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost"
          onClick={() => setWeekStart(addIsoDays(weekStart, 7))}
          aria-label="Next week"
        >
          Next →
        </button>
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost"
          onClick={() => setWeekStart(mondayIsoDate())}
        >
          This week
        </button>
      </div>

      {state.kind === 'loading' ? (
        <div className="ferro-myschedule__days" data-testid="my-schedule-skeleton">
          <Skeleton variant="table-row" />
          <Skeleton variant="table-row" />
          <Skeleton variant="table-row" />
        </div>
      ) : null}

      {state.kind === 'error' ? (
        <div className="ferro-dashboard__error" role="alert">
          <p>{state.message}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => void load()}
          >
            Try again
          </button>
        </div>
      ) : null}

      {state.kind === 'ready' ? (
        <>
          {shiftCount === 0 && activeTimeOff.length === 0 ? (
            <div className="ferro-empty-card" data-testid="my-schedule-empty">
              <p className="ferro-myschedule__empty-title">
                You’re not scheduled this week.
              </p>
              <p className="ferro-muted">
                Nothing is on your calendar for these seven days. Check another
                week with Prev / Next, or ask for time off below — your manager
                posts new shifts as they’re set.
              </p>
            </div>
          ) : (
            <ul className="ferro-myschedule__days" aria-label="My week">
              {weekDays.map(({ iso, label }) => {
                const dayShifts = shiftsByDate.get(iso) ?? [];
                const away = timeOffFor(iso);
                return (
                  <li
                    key={iso}
                    className={`ferro-myschedule__day${
                      iso === today ? ' ferro-myschedule__day--today' : ''
                    }`}
                    data-testid={`my-schedule-day-${iso}`}
                  >
                    <div className="ferro-myschedule__day-head">
                      <strong>{label}</strong>
                      <span className="ferro-muted">{formatDayShort(iso)}</span>
                      {iso === today ? (
                        <span className="ferro-badge ferro-badge--info">Today</span>
                      ) : null}
                    </div>

                    {away.map((r) => (
                      <div
                        key={r.id}
                        className={`ferro-timeoff-band ferro-timeoff-band--${
                          r.status === 'approved' ? 'approved' : 'pending'
                        }`}
                        data-testid="my-schedule-timeoff-band"
                      >
                        {typeLabel(r.type)}
                        {r.status === 'approved' ? ' — approved' : ' — waiting on a manager'}
                      </div>
                    ))}

                    {dayShifts.length === 0 ? (
                      away.length === 0 ? (
                        <p className="ferro-muted">Off</p>
                      ) : null
                    ) : (
                      <ul className="ferro-myschedule__shifts">
                        {dayShifts.map((s) => (
                          <li
                            key={s.id}
                            className="ferro-myschedule__shift"
                            data-testid="my-schedule-shift"
                          >
                            <span className="ferro-myschedule__shift-time">
                              {formatTimeRange(s.start_time, s.end_time)}
                            </span>
                            <JobFunctionChip role={String(s.role)} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          <section className="ferro-schedule-section" aria-label="My time off">
            <h2 className="ferro-schedule-section__title">My time off</h2>
            {cancelError ? (
              <div className="ferro-alert ferro-alert--error" role="alert">
                {cancelError}
              </div>
            ) : null}
            {myTimeOff.length === 0 ? (
              <p className="ferro-muted">
                You haven’t asked for any time off yet.
              </p>
            ) : (
              <ul className="ferro-table" aria-label="My time off requests">
                {myTimeOff.map((r) => (
                  <li
                    key={r.id}
                    className="ferro-table__row"
                    data-testid="my-time-off-row"
                  >
                    <div className="ferro-table__body">
                      <div className="ferro-table__primary">
                        {formatDayRange(r.start_date, r.end_date)}
                      </div>
                      <div className="ferro-table__meta">
                        {typeLabel(r.type)} ·{' '}
                        {r.status === 'pending'
                          ? 'Waiting on a manager'
                          : r.status === 'approved'
                            ? 'Approved'
                            : r.status === 'denied'
                              ? 'Denied'
                              : 'Withdrawn'}
                      </div>
                      {r.review_note ? (
                        <p className="ferro-timeoff-queue__note">
                          “{r.review_note}”
                        </p>
                      ) : null}
                    </div>
                    <div className="ferro-table__actions">
                      {r.status === 'pending' ? (
                        <button
                          type="button"
                          className="ferro-btn ferro-btn--ghost"
                          disabled={cancellingId === r.id}
                          onClick={() => void handleCancel(r)}
                        >
                          Withdraw
                        </button>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <TimeOffRequestForm
              apiUrl={apiUrl}
              primary
              onSubmitted={(request) =>
                setState((curr) =>
                  curr.kind === 'ready'
                    ? { ...curr, timeOff: [request, ...curr.timeOff] }
                    : curr,
                )
              }
            />
          </section>
        </>
      ) : null}
    </section>
  );
}

export default MySchedule;
